import React, { useState } from 'react';

const STAGES = ['Outreach','Follow-Up','Thank-You','Offer','Negotiation','Networking','Other'];

const BLANK = { name: '', stage: 'Follow-Up', subject: '', body: '' };

export default function TemplatesPage({ templates, API_URL, onUpdate }) {
  const [selected, setSelected] = useState(templates[0] || null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [modalOpen, setModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSelect = (t) => {
    setSelected(t);
    setEditing(false);
  };

  const handleNew = () => {
    setForm(BLANK);
    setModalOpen(true);
  };

  const handleEditSelected = () => {
    if (!selected) return;
    setForm({ name: selected.name, stage: selected.stage, subject: selected.subject, body: selected.body });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    const res = await fetch(`${API_URL}/api/templates/${selected.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const updated = await res.json();
    onUpdate(templates.map(t => t.id === selected.id ? updated : t));
    setSelected(updated);
    setEditing(false);
  };

  const handleCreate = async () => {
    const res = await fetch(`${API_URL}/api/templates`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const created = await res.json();
    onUpdate([...templates, created]);
    setSelected(created);
    setModalOpen(false);
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (selected.is_default) { alert('Cannot delete a default template.'); return; }
    if (!window.confirm('Delete this template?')) return;
    await fetch(`${API_URL}/api/templates/${selected.id}`, { method: 'DELETE' });
    const remaining = templates.filter(t => t.id !== selected.id);
    onUpdate(remaining);
    setSelected(remaining[0] || null);
  };

  const copyTemplate = async () => {
    if (!selected) return;
    await navigator.clipboard.writeText(`Subject: ${selected.subject}\n\n${selected.body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const stageColor = (s) => {
    const m = { 'Follow-Up':'#3b82f6','Thank-You':'#10b981','Offer':'#f59e0b','Outreach':'#8b5cf6','Negotiation':'#ef4444','Networking':'#06b6d4' };
    return m[s] || '#64748b';
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h2 className="page-title">Email Templates</h2>
          <p className="page-sub">{templates.length} template{templates.length !== 1 ? 's' : ''} · Variables: {'{{company}}'}, {'{{role}}'}, {'{{contact_name}}'}</p>
        </div>
        <button className="btn-primary" onClick={handleNew}>+ New Template</button>
      </div>

      {templates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✉️</div>
          <h3>No templates yet</h3>
          <p>Run the database init.sql to load 6 default templates, or create your own.</p>
        </div>
      ) : (
        <div className="templates-layout">
          {/* Left list */}
          <div className="template-list">
            {STAGES.map(stage => {
              const group = templates.filter(t => t.stage === stage);
              if (!group.length) return null;
              const color = stageColor(stage);
              return (
                <div key={stage} className="template-stage-group">
                  <div
                    className="template-stage-header"
                    style={{ borderLeftColor: color, color }}
                  >
                    {stage}
                    <span className="template-stage-count">{group.length}</span>
                  </div>
                  {group.map(t => (
                    <div
                      key={t.id}
                      className={`template-list-item ${selected?.id === t.id ? 'active' : ''}`}
                      style={selected?.id === t.id ? { borderLeftColor: color } : {}}
                      onClick={() => handleSelect(t)}
                    >
                      <div className="template-list-name">{t.name}</div>
                      {t.is_default && <span className="template-default-badge">Default</span>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Right preview/editor */}
          {selected ? (
            <div className="template-preview">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                {editing ? (
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={{ fontWeight: '700', fontSize: '1rem', border: 'none', outline: 'none', flex: 1 }}
                  />
                ) : (
                  <h3 style={{ fontSize: '1rem', fontWeight: '700' }}>{selected.name}</h3>
                )}
                <div style={{ display: 'flex', gap: '6px' }}>
                  {!editing ? (
                    <>
                      <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={copyTemplate}>
                        {copied ? '✓ Copied!' : '📋 Copy'}
                      </button>
                      <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={handleEditSelected}>Edit</button>
                      {!selected.is_default && (
                        <button className="btn-danger" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={handleDelete}>Delete</button>
                      )}
                    </>
                  ) : (
                    <>
                      <button className="btn-primary" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={handleSaveEdit}>Save</button>
                      <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={() => setEditing(false)}>Cancel</button>
                    </>
                  )}
                </div>
              </div>

              {editing ? (
                <>
                  <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select className="stage-select" value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}>
                      {STAGES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="template-subject-label">Subject Line</div>
                  <input
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '14px', outline: 'none' }}
                  />
                  <div className="template-subject-label">Body</div>
                  <textarea
                    className="template-body-area"
                    value={form.body}
                    onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                    rows={14}
                  />
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: '600', padding: '3px 10px', borderRadius: '10px', background: stageColor(selected.stage) + '20', color: stageColor(selected.stage) }}>
                      {selected.stage}
                    </span>
                  </div>
                  <div className="template-subject-label">Subject</div>
                  <div className="template-subject-val">{selected.subject}</div>
                  <div className="template-subject-label">Body</div>
                  <textarea className="template-body-area" value={selected.body} readOnly rows={14} style={{ cursor: 'default', background: '#fafafa' }} />
                </>
              )}
            </div>
          ) : (
            <div className="empty-state"><p>Select a template to preview</p></div>
          )}
        </div>
      )}

      {/* New template modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Template</h2>
              <button className="modal-close" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Template Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Interview Thank You" required />
                </div>
                <div className="form-group">
                  <label>Stage</label>
                  <select className="stage-select" value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}>
                    {STAGES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Subject Line *</label>
                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Following up on my application for {{role}} at {{company}}" required />
              </div>
              <div className="form-group">
                <label>Body</label>
                <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={10} placeholder={'Hi {{contact_name}},\n\n...'} />
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleCreate} disabled={!form.name || !form.subject}>Create Template</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
